import React, { useState } from "react";
import { useCourses } from "../hooks/useStudyOS";
import { useAuth } from "../contex/AuthContext";
import * as pdfjsLib from "pdfjs-dist";

// Use the correct worker for version 3.11.174
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

const CLAUDE_API_KEY = process.env.REACT_APP_CLAUDE_API_KEY;

async function extractPDFText(url) {
  try {
    console.log("Extracting PDF from:", url);
    const loadingTask = pdfjsLib.getDocument({
      url,
      cMapUrl: "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/cmaps/",
      cMapPacked: true,
    });
    const pdf = await loadingTask.promise;
    console.log("PDF loaded, pages:", pdf.numPages);
    let fullText = "";
    for (let i = 1; i <= Math.min(pdf.numPages, 20); i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map(item => item.str).join(" ");
      fullText += `[Page ${i}]: ${pageText}\n\n`;
    }
    console.log("Extracted text length:", fullText.length);
    return fullText;
  } catch (err) {
    console.error("PDF extraction failed:", err);
    return null;
  }
}

async function askClaude(systemPrompt, userMessage) {
  const response = await fetch("https://api.anthropic.com/v1/messages", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-api-key": CLAUDE_API_KEY,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true",
    },
    body: JSON.stringify({
      model: "claude-haiku-4-5-20251001",
      max_tokens: 2048,
      system: systemPrompt,
      messages: [{ role: "user", content: userMessage }],
    }),
  });

  if (!response.ok) {
    const errData = await response.json();
    console.error("Claude API error:", errData);
    throw new Error(errData?.error?.message || "API request failed");
  }

  const data = await response.json();
  return data.content[0].text;
}

export default function AIScreen({ note, onClose }) {
  const { profile } = useAuth();
  const { getCourse } = useCourses();
  const [mode, setMode] = useState(null);
  const [question, setQuestion] = useState("");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [error, setError] = useState("");
  const [selectedAttachments, setSelectedAttachments] = useState(
    (note?.attachments || []).map(a => ({ ...a, selected: true }))
  );

  const course = getCourse(note?.courseId);
  const getSelectedAtts = () => selectedAttachments.filter(a => a.selected);

  const toggleAttachment = (index) => {
    setSelectedAttachments(prev =>
      prev.map((a, i) => i === index ? { ...a, selected: !a.selected } : a)
    );
  };

  const buildContext = async () => {
    let context = "";

    if (note?.content) {
      context += `\n--- TYPED NOTES ---\n${note.content}\n`;
    }

    for (const att of getSelectedAtts()) {
      if (att.type === "application/pdf") {
        setLoadingMsg(`Reading ${att.name}...`);
        const text = await extractPDFText(att.url);
        if (text && text.trim().length > 50) {
          context += `\n--- PDF: ${att.name} ---\n${text}\n`;
        } else {
          context += `\n--- PDF: ${att.name} --- (scanned image - text not extractable)\n`;
        }
      }
    }

    return context;
  };

  const run = async (action) => {
    setMode(action);
    setLoading(true);
    setError("");
    setResult("");

    try {
      setLoadingMsg("Reading your materials...");
      const context = await buildContext();

      const hasContent = context.trim().length > 10;

      const systemPrompt = `You are an AI study assistant for a ${profile?.programme || "university"} student${profile?.school ? ` at ${profile.school}` : ""}.
${course ? `The student is studying ${course.name}${course.code ? ` (${course.code})` : ""}.` : ""}
Be concise, clear and educational.
${hasContent ? `Here are the student's study materials:\n${context}` : "Answer based on general knowledge for this subject."}`;

      let userMessage = "";

      if (action === "summarize") {
        setLoadingMsg("Summarizing...");
        userMessage = hasContent
          ? `Please analyze and summarize all the study materials provided. Structure with:
- **Key Concepts** — main ideas and definitions
- **Important Points** — critical details
- **Key Takeaways** — 3 most important things

Note title: ${note?.title}`
          : `Please provide a study summary on the topic: ${note?.title || course?.name || "this subject"}`;
      } else if (action === "quiz") {
        setLoadingMsg("Creating questions...");
        userMessage = hasContent
          ? `Generate 5 practice questions based on the study materials.
Mix multiple choice and short answer.
Number each question clearly.
Add "--- ANSWERS ---" section after with full explanations.
Note: ${note?.title}`
          : `Generate 5 practice questions on: ${note?.title || course?.name || "this subject"}`;
      } else {
        setLoadingMsg("Thinking...");
        userMessage = question;
      }

      const text = await askClaude(systemPrompt, userMessage);
      setResult(text);
    } catch (err) {
      console.error("AI error:", err);
      setError("Failed to get response. Please check your connection and try again.");
    }

    setLoading(false);
    setLoadingMsg("");
  };

  const sourceSummary = [
    note?.content ? "typed notes" : null,
    getSelectedAtts().length > 0 ? `${getSelectedAtts().length} PDF file${getSelectedAtts().length > 1 ? "s" : ""}` : null,
  ].filter(Boolean).join(" + ") || "course context";

  return (
    <div style={styles.overlay}>
      <div style={styles.container}>

        <div style={styles.header}>
          <div>
            <div style={styles.headerTitle}>🤖 AI Study Assistant</div>
            <div style={styles.headerSub}>{note?.title || "General assistant"}</div>
          </div>
          <button style={styles.closeBtn} onClick={onClose}>×</button>
        </div>

        {selectedAttachments.length > 0 && (
          <div style={styles.attSection}>
            <div style={styles.attLabel}>📎 Include in analysis:</div>
            <div style={styles.attList}>
              {selectedAttachments.map((att, i) => (
                <div key={i} style={styles.attItem} onClick={() => toggleAttachment(i)}>
                  <div style={{...styles.attCheck, ...(att.selected ? styles.attCheckOn : {})}}>
                    {att.selected && <span style={{fontSize:"10px", color:"#fff"}}>✓</span>}
                  </div>
                  <span style={styles.attName}>
                    {att.type === "application/pdf" ? "📄" : "🖼️"} {att.name}
                  </span>
                  <span style={styles.attSize}>{Math.round((att.size||0)/1024)}KB</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={styles.sourceBar}>
          <span style={styles.sourceText}>Analyzing: {sourceSummary}</span>
        </div>

        <div style={styles.actionRow}>
          <button
            style={{...styles.actionBtn, ...(mode==="summarize" ? styles.actionActive : {})}}
            onClick={() => run("summarize")} disabled={loading}>
            📋 Summarize
          </button>
          <button
            style={{...styles.actionBtn, ...(mode==="quiz" ? styles.actionActive : {})}}
            onClick={() => run("quiz")} disabled={loading}>
            🧠 Quiz me
          </button>
        </div>

        <div style={styles.askWrap}>
          <div style={styles.askRow}>
            <input
              style={styles.askInput}
              placeholder="Ask a question about your notes..."
              value={question}
              onChange={e => setQuestion(e.target.value)}
              onKeyDown={e => e.key === "Enter" && question.trim() && run("ask")}
              disabled={loading}
            />
            <button
              style={{...styles.askBtn, opacity: (!question.trim() || loading) ? 0.5 : 1}}
              onClick={() => run("ask")}
              disabled={loading || !question.trim()}>
              Ask
            </button>
          </div>
        </div>

        <div style={styles.resultWrap}>
          {loading ? (
            <div style={styles.loadingWrap}>
              <div style={styles.dots}>
                <span style={{...styles.dot, animationDelay:"0s"}}>●</span>
                <span style={{...styles.dot, animationDelay:"0.2s"}}>●</span>
                <span style={{...styles.dot, animationDelay:"0.4s"}}>●</span>
              </div>
              <div style={styles.loadingText}>{loadingMsg || "Processing..."}</div>
            </div>
          ) : error ? (
            <div style={styles.errorBox}>
              <div style={{fontWeight:600, marginBottom:"4px"}}>❌ Error</div>
              {error}
            </div>
          ) : result ? (
            <div style={styles.resultBox}>
              <div style={styles.resultLabel}>
                {mode === "summarize" ? "📋 Summary" :
                 mode === "quiz" ? "🧠 Practice Questions" :
                 "💬 Answer"}
              </div>
              <div style={styles.resultText}>{result}</div>
            </div>
          ) : (
            <div style={styles.emptyState}>
              <div style={{fontSize:"40px", marginBottom:"12px"}}>🤖</div>
              <div style={styles.emptyTitle}>Ready to help you study</div>
              <div style={styles.emptyDesc}>
                {selectedAttachments.length > 0
                  ? "Your files are selected — tap Summarize or Quiz me to analyze them."
                  : "Add notes or upload files, then use the tools above."}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay:       { position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", zIndex:300, display:"flex", alignItems:"flex-end", justifyContent:"center" },
  container:     { background:"var(--bg)", width:"100%", maxWidth:"560px", height:"92vh", borderRadius:"24px 24px 0 0", display:"flex", flexDirection:"column", overflow:"hidden" },
  header:        { display:"flex", justifyContent:"space-between", alignItems:"flex-start", padding:"20px 20px 14px", borderBottom:"1px solid var(--border)", flexShrink:0 },
  headerTitle:   { fontSize:"18px", fontWeight:700, color:"var(--text)" },
  headerSub:     { fontSize:"13px", color:"var(--text2)", marginTop:"3px" },
  closeBtn:      { width:"32px", height:"32px", borderRadius:"50%", background:"var(--surface2)", border:"none", fontSize:"20px", color:"var(--text)", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 },
  attSection:    { padding:"12px 20px", borderBottom:"1px solid var(--border)", flexShrink:0 },
  attLabel:      { fontSize:"11px", fontWeight:600, color:"var(--text3)", textTransform:"uppercase", letterSpacing:".06em", marginBottom:"8px" },
  attList:       { display:"flex", flexDirection:"column", gap:"6px" },
  attItem:       { display:"flex", alignItems:"center", gap:"10px", padding:"8px 12px", background:"var(--surface)", border:"1px solid var(--border)", borderRadius:"10px", cursor:"pointer" },
  attCheck:      { width:"18px", height:"18px", borderRadius:"4px", border:"1.5px solid var(--border2)", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center" },
  attCheckOn:    { background:"var(--accent)", borderColor:"var(--accent)" },
  attName:       { flex:1, fontSize:"13px", color:"var(--text)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" },
  attSize:       { fontSize:"11px", color:"var(--text3)", flexShrink:0 },
  sourceBar:     { padding:"8px 20px", background:"var(--accent-bg)", flexShrink:0 },
  sourceText:    { fontSize:"12px", color:"var(--accent2)", fontWeight:500 },
  actionRow:     { display:"flex", gap:"10px", padding:"12px 20px", flexShrink:0 },
  actionBtn:     { flex:1, padding:"13px", background:"var(--surface)", border:"1px solid var(--border)", borderRadius:"12px", cursor:"pointer", fontFamily:"inherit", fontSize:"14px", fontWeight:600, color:"var(--text2)", transition:"all .15s" },
  actionActive:  { background:"var(--accent-bg)", borderColor:"var(--accent-border)", color:"var(--accent2)" },
  askWrap:       { padding:"0 20px 12px", flexShrink:0 },
  askRow:        { display:"flex", gap:"8px" },
  askInput:      { flex:1, padding:"12px 14px", background:"var(--surface)", border:"1px solid var(--border2)", borderRadius:"10px", fontFamily:"inherit", fontSize:"14px", color:"var(--text)", outline:"none" },
  askBtn:        { padding:"12px 20px", background:"var(--accent)", border:"none", borderRadius:"10px", color:"#fff", fontFamily:"inherit", fontSize:"14px", fontWeight:600, cursor:"pointer" },
  resultWrap:    { flex:1, overflow:"auto", padding:"12px 20px 24px" },
  loadingWrap:   { display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", height:"180px", gap:"12px" },
  dots:          { display:"flex", gap:"8px" },
  dot:           { color:"var(--accent)", fontSize:"18px", animation:"bounce 1s infinite" },
  loadingText:   { fontSize:"14px", color:"var(--text2)", fontWeight:500 },
  errorBox:      { background:"var(--red-bg)", border:"1px solid var(--red)", borderRadius:"12px", padding:"16px", fontSize:"13px", color:"var(--red)", lineHeight:1.6 },
  resultBox:     { background:"var(--surface)", border:"1px solid var(--border)", borderRadius:"14px", padding:"18px" },
  resultLabel:   { fontSize:"11px", fontWeight:700, color:"var(--text3)", textTransform:"uppercase", letterSpacing:".08em", marginBottom:"12px" },
  resultText:    { fontSize:"14px", color:"var(--text)", lineHeight:1.8, whiteSpace:"pre-wrap", wordBreak:"break-word" },
  emptyState:    { textAlign:"center", padding:"40px 20px" },
  emptyTitle:    { fontSize:"16px", fontWeight:600, color:"var(--text)", marginBottom:"8px" },
  emptyDesc:     { fontSize:"13px", color:"var(--text2)", lineHeight:1.6 },
};